import React, { createContext, useContext, useState, useEffect } from 'react';
import { Notification, notificationHelpers } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  getUnreadCount: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationsProvider');
  }
  return context;
};

interface NotificationsProviderProps {
  children: React.ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const loadNotifications = async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    console.log('🔔 Carregando notificações...');
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await notificationHelpers.getUserNotifications();
      
      if (error) {
        console.error('❌ Erro ao carregar notificações:', error);
        setError('Erro ao carregar notificações');
        setNotifications([]);
      } else {
        console.log('✅ Notificações carregadas:', data?.length || 0);
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('❌ Erro inesperado ao carregar notificações:', error);
      setError('Erro inesperado ao carregar notificações');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshNotifications = async () => {
    await loadNotifications();
    await getUnreadCount();
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const result = await notificationHelpers.markAsRead(notificationId);
      if (result.success) {
        // Atualizar a notificação localmente
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true }
              : notification
          )
        );
        await getUnreadCount();
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const result = await notificationHelpers.markAllAsRead();
      if (result.success) {
        // Marcar todas as notificações como lidas localmente
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  const getUnreadCount = async () => {
    try {
      const { count, error } = await notificationHelpers.getUnreadCount();
      if (!error) {
        setUnreadCount(count);
      }
    } catch (error) {
      console.error('Erro ao contar notificações não lidas:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
    getUnreadCount();
  }, [user?.id]);

  const value: NotificationsContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}; 